"use client";

import React, { useEffect, useState } from "react";
import { Menu } from "lucide-react";
import { Link } from "@/components/common/Link";
import { WvcLogo } from "@/integrations/wordpress/WvcLogo";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
function DesktopMenu() {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex gap-6">
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-24 bg-muted animate-pulse rounded-md" />
        <div className="h-5 w-20 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <NavigationMenu viewport={false}>
      <NavigationMenuList className="gap-8">
        {menuItems.map((item, i) => <NavigationMenuItem key={item.id} data-index={i}>
            <NavigationMenuLink asChild>
              <Link to={item.href || "#"} className="text-sm font-medium text-foreground transition-colors hover:text-primary relative after:absolute after:bottom-[-4px] after:left-0 after:h-[2px] after:w-0 after:bg-primary after:transition-all hover:after:w-full">
                {item.label}
              </Link>
            </NavigationMenuLink>
          </NavigationMenuItem>)}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileMenu({
  setOpen
}: {
  setOpen: (open: boolean) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div className="flex flex-col gap-6 mt-8">
        <div className="h-6 w-32 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-40 bg-muted animate-pulse rounded-md" />
        <div className="h-6 w-28 bg-muted animate-pulse rounded-md" />
      </div>;
  }
  return <div className="flex flex-col gap-6 mt-8">
      {menuItems.map((item, i) => <Link key={item.id} data-index={i} to={item.href || "#"} className="text-lg font-medium text-foreground hover:text-primary transition-colors" onClick={() => setOpen(false)}>
          {item.label}
        </Link>)}
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 10);
    };
    window.addEventListener("scroll", handleScroll);
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  return <section data-section-id="252276" id="header" className={`sticky top-0 z-50 w-full transition-all duration-500 border-b ${isScrolled ? "bg-background/80 backdrop-blur-xl border-border/50 shadow-lg" : "bg-background border-transparent"}`}>
      <div className="container mx-auto px-4 md:px-6 h-20 flex items-center justify-between">
        {}
        <Link to="/HomePage" className="flex items-center shrink-0">
          <WvcLogo className="h-8 w-auto text-foreground" />
        </Link>

        {}
        <div className="hidden md:flex items-center gap-8 lg:gap-12">
          <MenuProvider menu_id="43">
            <DesktopMenu />
          </MenuProvider>
          
          <Button className="bg-gradient-to-br from-primary to-chart-4 text-primary-foreground hover:opacity-90 shadow-xl font-default font-bold px-8 transition-all hover:-translate-y-0.5 animate-pulse">
            Get Funded Now
          </Button>
        </div>

        {}
        <div className="md:hidden flex items-center">
          <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
            <SheetTrigger asChild>
              <Button variant="ghost" size="icon" className="text-foreground">
                <Menu className="h-6 w-6" />
                <span className="sr-only">Toggle menu</span>
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="w-[300px] sm:w-[400px] border-l border-border bg-background/95 backdrop-blur-md">
              <SheetHeader className="border-b border-border pb-4">
                <SheetTitle className="text-left">
                  <WvcLogo className="h-7 w-auto text-foreground" />
                </SheetTitle>
              </SheetHeader>
              
              <MenuProvider menu_id="43">
                <MobileMenu setOpen={setMobileMenuOpen} />
              </MenuProvider>
              
              <div className="mt-8 flex flex-col gap-4">
                <Button className="w-full bg-gradient-to-br from-primary to-chart-4 text-primary-foreground hover:opacity-90 shadow-lg font-default font-bold" onClick={() => setMobileMenuOpen(false)}>
                  Get Funded Now
                </Button>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </div>
    </section>;
}